import { usePublicAboutStats } from "@bodhistreams/queries";
import { Logo, VideoBackground } from "@bodhistreams/ui";
import { Divider, Typography, theme } from "antd";
import PageLayout from "./PageLayout";

const { Title, Paragraph } = Typography;
const { useToken } = theme;
const DEFAULT_GENRES_COUNT = 28;

const getWholeHourCount = (seconds) => {
	if (!Number.isFinite(seconds) || seconds <= 0) {
		return null;
	}

	return Math.max(1, Math.floor(seconds / 3600));
};

const AboutHero = () => {
	const { token } = useToken();
	const { data: aboutStats } = usePublicAboutStats();

	const genresCount = Number.isFinite(aboutStats?.genres_count)
		? aboutStats.genres_count
		: DEFAULT_GENRES_COUNT;
	const totalHours = getWholeHourCount(aboutStats?.total_duration_seconds);
	const formattedGenresCount = genresCount.toLocaleString();
	const formattedHours = totalHours?.toLocaleString();

	return (
		<div style={{ position: "relative" }}>
			<VideoBackground
				showBlueGradient={false}
				showTopWhiteGradient={false}
				showBottomWhiteGradient={false}
				showVerticalGradient={true}
				darkOverlayOpacity="rgba(0, 0, 0, 0.1)"
				containerStyle={{
					padding: "120px 24px 80px",
					textAlign: "center",
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
					alignItems: "center",
				}}
				contentStyle={{
					maxWidth: "800px",
				}}
			>
				<div style={{ maxWidth: "800px", position: "relative", zIndex: 1 }}>
					<Logo.Favicon hyperlink={true} size={80} />
					<Title
						level={2}
						style={{
							marginTop: "20px",
							marginBottom: "24px",
							color: token.colorText,
							fontSize: "32px",
						}}
					>
						About Bodhistreams
					</Title>
					<Paragraph
						style={{
							fontSize: "16px",
							marginBottom: "24px",
							maxWidth: "600px",
							margin: "0 auto 24px",
							color: token.colorTextSecondary,
						}}
					>
						Discover the journey behind our mindful music compositions
					</Paragraph>

					<div
						style={{
							textAlign: "center",
							maxWidth: "700px",
							margin: "40px auto 0",
						}}
					>
						<Title
							level={4}
							style={{
								marginBottom: "24px",
								color: token.colorPrimary,
								fontWeight: "600",
								letterSpacing: "1px",
								textTransform: "uppercase",
							}}
						>
							Wellness Music
						</Title>
						<Paragraph
							style={{
								fontSize: "16px",
								lineHeight: "1.8",
								color: token.colorTextSecondary,
								marginBottom: "32px",
							}}
						>
							BodhiStreams delivers original, fully licensed wellness and spa
							music that meets the highest industry standards. Our music is
							streamed to your business through a secure and reliable platform,
							supported by dedicated technical assistance whenever you need it.
						</Paragraph>

						<Divider style={{ margin: "40px 0" }} />

						<Title
							level={4}
							style={{
								marginBottom: "24px",
								color: token.colorPrimary,
								fontWeight: "600",
								letterSpacing: "1px",
								textTransform: "uppercase",
							}}
						>
							Plug & Play
						</Title>
						<Paragraph
							style={{
								fontSize: "16px",
								lineHeight: "1.8",
								color: token.colorTextSecondary,
								marginBottom: "32px",
							}}
						>
							Our easy-to-use, plug-and-play system allows you to effortlessly
							create immersive sound environments for your guests. Enhance every
							stage of their journey with carefully curated music designed to
							complement and elevate relaxation, balance, and overall wellbeing.
						</Paragraph>

						<Divider style={{ margin: "40px 0" }} />

						<Title
							level={4}
							style={{
								marginBottom: "24px",
								color: token.colorPrimary,
								fontWeight: "600",
								letterSpacing: "1px",
								textTransform: "uppercase",
							}}
						>
							Multi-Zone Streaming
						</Title>
						<Paragraph
							style={{
								fontSize: "16px",
								lineHeight: "1.8",
								color: token.colorTextSecondary,
								marginBottom: "32px",
							}}
						>
							With BodhiStreams, you can stream different music in multiple
							areas of your facility at the same time — perfect for tailoring
							the atmosphere in treatment rooms, relaxation spaces, and common
							areas. Simply connect a tablet or device via Wi-Fi or Bluetooth to
							your speakers, and your space is ready to go.
						</Paragraph>

						<Divider style={{ margin: "40px 0" }} />

						<Title
							level={4}
							style={{
								marginBottom: "24px",
								color: token.colorPrimary,
								fontWeight: "600",
								letterSpacing: "1px",
								textTransform: "uppercase",
							}}
						>
							Therapeutic Design
						</Title>
						<Paragraph
							style={{
								fontSize: "16px",
								lineHeight: "1.8",
								color: token.colorTextSecondary,
								marginBottom: "32px",
							}}
						>
							High-quality music creates a more memorable guest experience. Our
							compositions are designed to promote calm, support therapeutic
							treatments, and maintain a gentle rhythmic flow throughout each
							session.
						</Paragraph>

						<Divider style={{ margin: "40px 0" }} />

						<Title
							level={4}
							style={{
								marginBottom: "24px",
								color: token.colorPrimary,
								fontWeight: "600",
								letterSpacing: "1px",
								textTransform: "uppercase",
							}}
						>
							{formattedGenresCount} Genres & Growing
						</Title>
						<Paragraph
							style={{
								fontSize: "16px",
								lineHeight: "1.8",
								color: token.colorTextSecondary,
								marginBottom: "32px",
							}}
						>
							BodhiStreams offers {formattedGenresCount} specialized music
							genres crafted specifically for wellness and spa environments,
							{formattedHours
								? ` and over ${formattedHours} hours of high-quality audio`
								: " along with a growing library of high-quality audio"}{" "}
							helping you create a peaceful, harmonious, and restorative
							atmosphere for every space. We periodically add new tracks to our
							collections, ensuring the music stays fresh and always flowing.
						</Paragraph>

						<Divider style={{ margin: "40px 0" }} />

						<Title
							level={4}
							style={{
								marginBottom: "16px",
								color: token.colorPrimary,
								fontWeight: "600",
								letterSpacing: "1px",
								textTransform: "uppercase",
							}}
						>
							432 Hz Audio Quality
						</Title>
						<Paragraph
							style={{
								fontSize: "18px",
								lineHeight: "1.8",
								color: token.colorText,
								fontWeight: "500",
								fontStyle: "italic",
							}}
						>
							All BodhiStreams music is produced in 432 Hz and mastered to the
							highest audio quality standards.
						</Paragraph>
					</div>
				</div>
			</VideoBackground>
		</div>
	);
};

const About = () => {
	return (
		<PageLayout fullWidthContent={<AboutHero />}>
			{/* Empty content section since all content is now in the Hero */}
		</PageLayout>
	);
};

export default About;
